import { GetServerSideProps } from 'next';
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  SimpleGrid,
  VisuallyHidden,
  Text,
} from '@chakra-ui/react';
import NextHead from 'next/head';
import React from 'react';
import isMobileDevice from 'ismobilejs';
import NextLink from 'next/link';
import NextImage from 'next/image';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { title } from '../../common/libs/metadata';
import { getPublicRuntimeConfig } from '../../common/config';
import FullWidthBox from '../../components/FullWidthBox';
import withAuth from '../../server/libs/withAuth';
import * as date from '../../server/libs/date';
import { NextPageWithLayout } from '../../common/types';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import ArticleSummary, { ArticleHeader } from '../../components/ArticleSummary';
import { ArticleStatus, shouldNotShow } from '../../common/libs/article';
import { fetchAccountIntegrationProfiles } from '../../server/libs/id';
import prisma from '../../server/libs/prisma';
import { DiscloseScope } from '../../common/libs/discloseScope';

const GA_CATEGORY = 'docs_academy';

const { slideWebBaseUrl, qaWebBaseUrl } = getPublicRuntimeConfig();

const pageTitle = '実例とテンプレから学ぶ！スライド作成完全ガイド';
const pageDescription =
  'このページでは、先生方がまとめたスライド作成の基礎&実践のノウハウや参考例、ガイドラインや簡単につくるヒント、抑えておくべき著作権に関する知識、 Antaa Slide投稿者の成功体験などをご紹介し、みなさんのスライド作成をサポートします。';

type PageContext = {
  isMobile: boolean;
  articles: ArticleHeader[];
};

const tableOfContentsItems = [
  {
    title: 'スライド作成の基礎知識',
    notes: [
      'ストーリー設計を学ぶ',
      'デザインの工夫を学ぶ',
      '著作権について学ぶ',
    ],
  },
  {
    title: 'スライド作成の実例',
    notes: [
      'シンプルで分かりやすい例',
      'テンプレートを使った例',
      '研修向けの例',
      '働き方紹介の例',
      'その他',
    ],
  },
  {
    title: '投稿者インタビュー',
  },
  {
    title: '投稿してみよう！',
  },
];

type SectionProps = {
  title: string;
  description?: string;
  chapters?: {
    title?: string;
    description?: string;
    articles?: ({ articleId: string } | ExternalLinkCardProps)[];
    children?: React.ReactNode;
  }[];
  children?: React.ReactNode;
};

const sections: SectionProps[] = [
  {
    title: 'スライド作成の基礎知識',
    description:
      'まずはスライド作成の基礎知識として、ストーリー設計、見やすいデザインやテンプレートの使い方、そして医療スライドでは避けては通れない著作権のお話までを、実際の医師が作成したスライドを元に、学んでいきましょう。',
    chapters: [
      {
        title: 'ストーリー設計を学ぶ',
        description:
          '読みやすく勉強になるスライドがたくさんあります。しかし、いざ自分で作ってみると、思うような仕上がりになりません。実は、メッセージが作り込まれていないことが主な理由です。スライドのメッセージを洗練させる3つの法則を、説明します。',
        articles: [
          {
            url: 'https://note.com/antaa/n/n9b3c87abedf5',
            name: '【note】伝わるスライドを作るにはメッセージを洗練させよう　3つの法則',
            topImage: '/images/docs/academy_antaa_note_3rules.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'note_3rules',
          },
        ],
      },
      {
        title: 'デザインの工夫を学ぶ',
        description:
          'より多くの先生に見てもらえるスライドには共通点があります。テーマが読者を引きつけるものであり、さらにデザインがすっきりと見やすいことです。今回は、分かりやすいスライドを作成されている先生方に、デザインの工夫についてお話を伺いました。',
        articles: [
          {
            url: 'https://note.com/antaa/n/ncf610439ca85',
            name: '【note】デザインセンスがなくても見やすいスライドを作る方法',
            topImage: '/images/docs/academy_antaa_note_design_method.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'note_design',
          },
          {
            articleId: 'f5a051712edc4602',
          },
          {
            articleId: 'eb7d294a5dc446df',
          },
        ],
      },
      {
        title: '著作権について学ぶ',
        description:
          'スライド作成の際に注意すべき著作権などの基本的なポイントについて、リンクパートナーズ法律事務所の弁護士・菅沼 匠先生に分かりやすく解説いただきました。さらに、Antaa Slideにおける知的財産の取り扱い方針についても丁寧に説明いただいています。',
        articles: [
          {
            articleId: 'f830e19d248d40a4',
          },
          {
            url: 'https://qa.antaa.jp/stream/contents/312',
            name: '知っておきたい！スライド作成のポイント（41分）',
            topImage: '/images/docs/academy_antaa_streaming_slide_point.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'slide_point_movie',
          },
        ],
      },
    ],
  },
  {
    title: 'スライド作成の実例',
    description:
      'ページ数が少なめのシンプルなスライド、テンプレートを使って作成されたスライド、院内勉強会向けのスライドや、ユニークなテーマのスライドなど、シェアされているスライドの一部をご紹介します。',
    chapters: [
      {
        title: 'シンプルで分かりやすい例',
        description: `少ないページ数で分かりやすくまとめられたスライドの実例です。
まずは要点だけを記述したシンプルなスライドからはじめて見るのもおすすめです。`,
        articles: [
          {
            articleId: '8df02857a1dc4dac',
          },
        ],
      },
      {
        title: 'テンプレートを使ったスライド作成例',
        description: `Antaa Slideのテンプレートを活用したスライドを紹介します。心電図の解釈を整理したもの、肺炎が治らないときの着眼点を語呂で示したもの、そして産業医になるための道筋を描いたもの。テーマは違っても、どれも「伝えたいことを誰にでもわかりやすく整理できる」好例です。テンプレートを使えば、自分のスライドもぐっと魅力的に仕上げられることが実感できます。`,
        articles: [
          {
            articleId: '4884d55a9e224c11',
          },
          {
            articleId: '3b5b8a77811e4072',
          },
          {
            articleId: 'fc121bfec9d141ee',
          },
        ],
        children: (
          <Center py={2}>
            <Button
              as="a"
              target="_blank"
              rel="noopener noreferrer"
              href={`${qaWebBaseUrl}/auth/redirect?type=slide-template`}
              colorScheme="brand"
              size="lg"
              onClick={() =>
                sendAnalyticsEvent(GA_CATEGORY, 'click', 'template_form')
              }
            >
              テンプレート申請フォームへ
            </Button>
          </Center>
        ),
      },
      {
        title: '研修向けの例',
        description: `院内でのコミュニケーションをテーマとした、初期研修医の教育向けスライドの実例です。
学習のポイントを1ページ内に簡潔にまとめると、繰り返し見返す際に便利です。`,
        articles: [
          {
            articleId: '26edc39b8aed450f',
          },
        ],
      },
      {
        title: '働き方紹介の例',
        description:
          '医師のキャリアには、多様な働き方があります。家庭や子育てと両立しながら開業を目指す道もあれば、フリーランスとして柔軟な働き方を選ぶ方法もあります。今回ご紹介するスライドでは、そうした「医師の働き方」の一例として、子育てと仕事を両立させながらキャリアを築いた経験や、麻酔科医がフリーランスやアルバイトという形で働く実情がまとめられています。自分に合った働き方を考えるうえでの具体的なヒントが得られる内容です。',
        articles: [
          {
            articleId: '6322838b4ea44fbd',
          },
          {
            articleId: '68f800747f8b4a28',
          },
        ],
      },
      {
        title: 'その他',
        description: `クリスマスの小児科事情に関するユニークな仮説をテーマにしたスライドです。
息抜きにちょうど良いテーマや、読んでいてホッコリするテーマなど、楽しみながらスライドを作ってみるのもおすすめです。`,
        articles: [
          {
            articleId: '93153e18285f47ac',
          },
        ],
      },
    ],
  },
  {
    title: '投稿者インタビュー',
    description:
      'スライドをシェアしたことによる予想外の反響や、あらたに生まれた他の医師とのつながり。まずアウトプットして第一歩を踏み出すことの大切さ。スライドをブラッシュアップすることが自身の成長に繋がる様子など、スライドシェアによって得られた様々な体験についてお話いただきました。',
    chapters: [
      {
        articles: [
          {
            url: 'https://note.com/antaa/n/ne14b431f81f4?magazine_key=m3b899fb4c8fe',
            name: '【note】「分かった気になる」ことの大切さ。入門書としてのスライド作り【Antaa Slideインタビュー】音成 秀一郎先生（広島大学病院）',
            topImage: '/images/docs/academy_antaa_note_interview01.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'note_interview01',
          },
          {
            url: 'https://note.com/antaa/n/n7d19da9450d8?magazine_key=m3b899fb4c8fe',
            name: '【note】スライド投稿をきっかけに広がる医師同士の交流【Antaa Slideインタビュー】西澤 俊紀先生（聖路加国際病院）',
            topImage: '/images/docs/academy_antaa_note_interview02.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'note_interview02',
          },
          {
            url: 'https://note.com/antaa/n/n65c92c1e47d9?magazine_key=m3b899fb4c8fe',
            name: '【note】スライドは自分フィルターを通して伝えるメディア 【Antaa Slideインタビュー】山本 大介先生（湘南鎌倉総合病院）',
            topImage: '/images/docs/academy_antaa_note_interview03.png',
            author: {
              userName: 'Antaa運営事務局',
              profilePhoto: '/images/icon_antaa.svg',
            },
            gaEventLabel: 'note_interview03',
          },
        ],
        children: (
          <Button
            as="a"
            href="https://note.com/antaa/m/m3b899fb4c8fe"
            bg="white.500"
            color="brand.500"
            height={8}
            fontSize={14}
            _hover={{
              bg: 'white.500',
              textDecoration: 'underline',
            }}
            onClick={() =>
              sendAnalyticsEvent(GA_CATEGORY, 'click', 'note_interview')
            }
            mb={4}
            w="100%"
            p={4}
            border="1px solid"
            borderColor="brand.500"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text pr={2}>もっと見る</Text>
            <ChevronDownIcon />
          </Button>
        ),
      },
    ],
  },
  {
    title: '投稿してみよう！',
    description:
      '誰もが最初は不安や迷いを抱えながら、試行錯誤の中でスライドを作ってきました。あなたの経験や工夫が詰まったその一枚が、これから学ぶ誰かにとって大きな気づきや支えになります。Antaa Slideは、医師同士が知識を分かち合い、ともに成長していく場所です。完璧である必要はありません。今のあなたが伝えられることを、ぜひスライドにして共有してみませんか？',
    children: (
      <Center py={2}>
        <UploadButton />
      </Center>
    ),
  },
];

const TableOfContents = ({
  isMobile,
  chapters,
}: {
  isMobile: boolean;
  chapters: { title: string; notes?: string[] }[];
}) => {
  return (
    <List py={1}>
      {chapters.map(({ title, notes }, index) => {
        const id = `chapter-${index + 1}`;
        return (
          <ListItem
            key={title}
            as="a"
            href={`#${id}`}
            onClick={(e) => {
              e.preventDefault();
              const target = document.getElementById(id);
              if (target) {
                window.scrollTo({
                  top: target.offsetTop - 70, // ヘッダーの高さ分調整
                  behavior: 'smooth',
                });
              }
            }}
            py={0.5}
            fontSize="sm"
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="flex-start"
            cursor="pointer"
          >
            <Flex
              color="primaryDark"
              fontWeight="bold"
              w={200}
              flexShrink={0}
              gap={1}
              fontSize="md"
            >
              <Text>{index + 1}.</Text>
              <Text textDecoration="underline">{title}</Text>
            </Flex>
            <Flex
              flexWrap="wrap"
              justifyContent="flex-start"
              ml={isMobile ? '1em' : 0}
              fontSize="md"
            >
              {notes ? (
                <Box>
                  {notes.map((text) => (
                    <Text
                      key={text}
                      as="span"
                      display={isMobile ? 'list-item' : 'inline-block'}
                      pr={1}
                    >
                      ・{text}
                    </Text>
                  ))}
                </Box>
              ) : (
                <Box>{notes}</Box>
              )}
            </Flex>
          </ListItem>
        );
      })}
    </List>
  );
};

const SubHeader = ({
  children,
  isMobile,
}: {
  children: string;
  isMobile: boolean;
}) => {
  return (
    <Flex
      as="h3"
      fontSize={isMobile ? 'lg' : 'xl'}
      fontWeight="bold"
      alignItems="center"
    >
      <NextImage
        src="/images/icon_md_lightbulb.svg"
        alt=""
        width={24}
        height={24}
      />
      {children}
    </Flex>
  );
};

type ExternalLinkCardProps = {
  url: string;
  name: string;
  topImage: string;
  gaEventLabel: string;
  author: {
    userName: string;
    profilePhoto: string;
  };
};

const ExternalLinkCard = ({
  url,
  name,
  topImage,
  author,
  gaEventLabel,
  isMobile,
}: ExternalLinkCardProps & {
  isMobile: boolean;
}) => {
  return (
    <Link
      href={url}
      isExternal
      _hover={{
        textDecoration: 'none',
      }}
      onClick={() => sendAnalyticsEvent(GA_CATEGORY, 'click', gaEventLabel)}
    >
      <Box
        boxShadow="0 0 8px rgba(0,0,0,0.08)"
        borderRadius="lg"
        pos="relative"
        overflow="hidden"
        h="full"
        display={isMobile ? 'flex' : 'block'}
        flexDir={isMobile ? 'row-reverse' : undefined}
        pr={isMobile ? 4 : 0}
      >
        <Image
          src={topImage}
          alt={name}
          w={isMobile ? 118 : 'full'}
          h="auto"
          objectFit="contain"
        />
        <Box
          color="textPrimary"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={2}
          py={2}
          px={4}
        >
          <Text
            fontWeight="bold"
            fontSize="sm"
            noOfLines={isMobile ? 2 : undefined}
          >
            {name}
          </Text>
          <Box display="flex" alignItems="center" gap={1}>
            <Image
              p={1}
              src={author.profilePhoto}
              alt={author.userName}
              width={8}
              height={8}
              borderRadius="full"
            />
            <Text fontSize="sm">{author.userName}</Text>
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

const ChapterArticles = ({
  slideArticles,
  articles,
  isMobile,
}: {
  slideArticles: ArticleHeader[];
  articles: ({ articleId: string } | ExternalLinkCardProps)[];
  isMobile: boolean;
}) => {
  return (
    <SimpleGrid columns={[1, 2, 3]} spacing={4} w="full">
      {articles.map((article) => {
        if ('articleId' in article) {
          const { articleId } = article;
          const slideArticle = slideArticles.find(
            (a) => a.articleId === articleId,
          );
          if (slideArticle) {
            return (
              <ArticleSummary
                key={`${articleId}-${slideArticle.status}`}
                {...slideArticle}
                layoutType={isMobile ? 'horizontal' : 'vertical'}
                gaEventLabel={'academy_article'}
              />
            );
          }
        } else {
          return (
            <ExternalLinkCard
              key={article.url}
              {...article}
              isMobile={isMobile}
            />
          );
        }
      })}
    </SimpleGrid>
  );
};

function UploadButton() {
  return (
    <NextLink href={'/article/upload'} passHref>
      <Button
        colorScheme="brand"
        size="lg"
        onClick={() => sendAnalyticsEvent(GA_CATEGORY, 'click', 'upload')}
      >
        スライドを投稿してみる
      </Button>
    </NextLink>
  );
}

const Section = ({
  title,
  description,
  isMobile,
  number,
  chapters,
  children,
  slideArticles,
}: SectionProps & {
  number: number;
  isMobile: boolean;
  slideArticles: ArticleHeader[];
}) => (
  <Box mb={6}>
    <Heading
      as="h2"
      id={`chapter-${number}`}
      fontSize={isMobile ? '2xl' : '3xl'}
      fontWeight="bold"
      py={1}
      px={4}
      mt={6}
      borderLeft="4px solid"
      borderColor="primaryMain"
    >
      {number}. {title}
    </Heading>
    <Box py={2} mb={4}>
      {description}
    </Box>
    {chapters?.length &&
      chapters.map((chapter, index) => (
        <Box key={`${number}-${index}`} mb={4}>
          {chapter.title && (
            <SubHeader isMobile={isMobile}>{chapter.title}</SubHeader>
          )}
          {chapter.description && (
            <Box py={2} whiteSpace="pre-wrap">
              {chapter.description}
            </Box>
          )}

          {!!chapter?.articles?.length && (
            <Box pb={4}>
              <ChapterArticles
                slideArticles={slideArticles}
                articles={chapter.articles}
                isMobile={isMobile}
              />
            </Box>
          )}
          {chapter?.children && <Box py={2}>{chapter.children}</Box>}
        </Box>
      ))}
    {children && <Box py={2}>{children}</Box>}
  </Box>
);

const AcademyPage: NextPageWithLayout<PageContext> = ({
  isMobile,
  articles: slideArticles,
}: PageContext) => {
  const canonicalUrl = `${slideWebBaseUrl}/docs/academy`;

  return (
    <>
      <NextHead>
        <title>{title(pageTitle)}</title>
        <meta name="description" content={pageDescription} key="description" />
        <meta property="og:title" content={title(pageTitle)} key="og:title" />
        <meta
          property="og:description"
          content={pageDescription}
          key="og:description"
        />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        <meta
          property="og:image"
          content={`${slideWebBaseUrl}/images/docs_academy_banner_1300x720.png`}
          key="og:image"
        />
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <VisuallyHidden as="h1">{pageTitle}</VisuallyHidden>
      <Box>
        <Image
          src={
            isMobile
              ? '/images/docs/academy_head_mobile.png'
              : '/images/docs/academy_head_pc.png'
          }
          alt="実例とテンプレから学ぶ！スライド作成完全ガイド 学会・研修・勉強会にすぐ使えるノウハウ 基礎知識から実例紹介まで！"
          w="full"
        />
      </Box>
      <FullWidthBox bg="#EEF6ED" py={6}>
        <Container>
          <Box textAlign="center" mb={4}>
            <UploadButton />
          </Box>
          <Box
            py={6}
            px={isMobile ? 2 : 8}
            backgroundColor="white"
            mx={{
              base: 0,
              lg: -2,
            }}
          >
            <Heading as="h2" fontSize="2xl">
              スライド、どう作れば伝わるのか悩んでいませんか？
            </Heading>
            <Box fontWeight="bold" py={2}>
              {pageDescription}
            </Box>
            <Box fontSize="xs" fontWeight="bold">
              このページで学べること
            </Box>
            <TableOfContents
              isMobile={isMobile}
              chapters={tableOfContentsItems}
            />
          </Box>
        </Container>
      </FullWidthBox>
      <Container py={6}>
        {sections.map((section, index) => (
          <Section
            key={index}
            {...section}
            number={index + 1}
            isMobile={isMobile}
            slideArticles={slideArticles}
          />
        ))}
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;

    const articleIds = sections
      .flatMap(
        (section) =>
          section.chapters?.flatMap(
            (chapter) =>
              chapter.articles?.flatMap((article) =>
                'articleId' in article ? article.articleId : [],
              ) ?? [],
          ) ?? [],
      )
      .filter((id) => !!id);

    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIds,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        create_date: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    const articles: ArticleHeader[] = articleHeaders.reduce(
      (articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: author.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds: author.update_date.getTime(),
            profilePhotoS3Key: author.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      },
      [],
    );

    return {
      props: {
        isMobile,
        articles,
      },
    };
  },
);

export default AcademyPage;
